/**
 * レセプト内容一覧 (Receipt Content List) API service.
 * Shared by A2 + R2 via X-Service-Type header.
 */

import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import type {
  MassagerOption,
  RezeptHistoriesExportCsvPayload,
  RezeptHistoriesInitResponse,
  RezeptHistoriesSearchPayload,
  RezeptHistoriesSearchResult,
} from '@/pages/main/rezeptHistories/types';

interface ApiEnvelope<T> {
  success: boolean;
  message: string;
  data: T;
}

/** Strip nullish/empty values so they don't override server defaults. */
function pruneParams<T extends Record<string, unknown>>(input: T): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input)) {
    if (value === null || value === undefined || value === '') continue;
    out[key] = value;
  }
  return out;
}

/**
 * GET /init — defaults + insurer_types + ini_options.
 * Not service-type-specific (lookup data).
 */
export async function getRezeptHistoriesInit(
  siteType: string,
  clinicNumber: string
): Promise<RezeptHistoriesInitResponse> {
  const response = await apiClient.get<ApiEnvelope<RezeptHistoriesInitResponse>>(
    clinicApiPath(siteType, clinicNumber, '/rezept-histories/init')
  );
  return response.data.data;
}

/**
 * GET /search — paginated list with filters.
 */
export async function searchRezeptHistories(
  siteType: string,
  clinicNumber: string,
  payload: RezeptHistoriesSearchPayload,
  serviceType: string
): Promise<RezeptHistoriesSearchResult> {
  const response = await apiClient.get<ApiEnvelope<RezeptHistoriesSearchResult>>(
    clinicApiPath(siteType, clinicNumber, '/rezept-histories/search'),
    {
      params: pruneParams(payload as unknown as Record<string, unknown>),
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data.data;
}

/**
 * GET /all-ids — fetch all matching IDs across pages (for 全選択 header checkbox).
 * Same filter params as /search; ignores pagination + sort.
 */
export async function getRezeptHistoriesAllIds(
  siteType: string,
  clinicNumber: string,
  payload: RezeptHistoriesSearchPayload,
  serviceType: string
): Promise<number[]> {
  const response = await apiClient.get<ApiEnvelope<{ ids: number[] }>>(
    clinicApiPath(siteType, clinicNumber, '/rezept-histories/all-ids'),
    {
      params: pruneParams(payload as unknown as Record<string, unknown>),
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data.data.ids;
}

/**
 * GET /massagers — dependent dropdown (reloads on 提出年月 change).
 */
export async function getRezeptHistoriesMassagers(
  siteType: string,
  clinicNumber: string,
  yearmonth: string,
  serviceType: string
): Promise<MassagerOption[]> {
  const response = await apiClient.get<ApiEnvelope<{ data: MassagerOption[] }>>(
    clinicApiPath(siteType, clinicNumber, '/rezept-histories/massagers'),
    {
      params: { search_yearmonth: yearmonth },
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data.data.data;
}

/**
 * POST /export-csv — returns the CSV file as a Blob (UTF-8 BOM, attached `Content-Disposition`).
 * Caller is responsible for triggering the browser download (see useExportRezeptHistoriesCsvMutation).
 */
export async function exportRezeptHistoriesCsv(
  siteType: string,
  clinicNumber: string,
  payload: RezeptHistoriesExportCsvPayload,
  serviceType: string
): Promise<Blob> {
  const response = await apiClient.post<Blob>(
    clinicApiPath(siteType, clinicNumber, '/rezept-histories/export-csv'),
    payload,
    {
      responseType: 'blob',
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data;
}
